/**
 * Builds the `@font-face` CSS used when rasterising the poster to PNG.
 *
 * The rasteriser inlines the page into an SVG, and an SVG image cannot reach out to
 * the network — so any web font has to travel with it as a data URI. html-to-image
 * would normally assemble this itself by reading `document.styleSheets`, but the fonts
 * come from fonts.googleapis.com and reading `cssRules` on a cross-origin sheet throws
 * a SecurityError. Fetching the same stylesheet is allowed, so that is what we do here,
 * then swap every font URL for its base64 payload.
 */

/** Resolved once per page load — the fonts do not change underneath us. */
let cached: Promise<string> | null = null;

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  // Chunked so a multi-hundred-KB font cannot overflow the argument list.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

async function build(): Promise<string> {
  const link = document.querySelector<HTMLLinkElement>(
    'link[rel="stylesheet"][href*="fonts.googleapis.com"]',
  );
  if (!link) return '';

  const css = await fetch(link.href).then((response) => response.text());
  const urls = [...new Set([...css.matchAll(/url\((https:\/\/[^)]+)\)/g)].map((m) => m[1]))];

  const embedded = await Promise.all(
    urls.map(async (url) => {
      const buffer = await fetch(url).then((response) => response.arrayBuffer());
      return [url, `data:font/woff2;base64,${toBase64(buffer)}`] as const;
    }),
  );

  let out = css;
  for (const [url, dataUri] of embedded) out = out.split(url).join(dataUri);
  return out;
}

/**
 * The stylesheet with every font inlined, or an empty string if it cannot be built.
 * An empty result is not fatal: the export still renders, just in a fallback face.
 */
export function posterFontCss(): Promise<string> {
  if (!cached) {
    cached = build().catch(() => '');
  }
  return cached;
}
